"use client"

import {
    selectActiveBmSubId,
    selectActiveBrandId,
    selectActivePpSubId,
    useAppSelector
} from "services"
import type { OrganizationGroup } from "types"
import useOrgGroupRoutes from "../useOrgGroupRoutes/useOrgGroupRoutes"

interface IFeatureRoutes {
    brewKnowledgeFeatureRoute: () => string
    brewMenuFeatureRoute: () => string
    marketMyBreweryFeatureRoute: () => string
    performancePlatformFeatureRoute: () => string
    brewMenuFeatureSubscriptionRoute: (group: OrganizationGroup) => string
    marketMyBreweryFeatureSubscriptionRoute: (group: OrganizationGroup) => string
    performancePlatformSubscriptionRoute: (group: OrganizationGroup) => string
    brewKnowledgeSubscriptionRoute: (group: OrganizationGroup) => string
}

const salesOrId = (id: string) => (id && id.length > 0 ? id : "unsubscribed")

/**
 * Hook that provides direct root routes for the feature groups: brew knowledge, brewmenu, performance platform, and market my brewery.
 * If a user is not in the context of a user group, the application root will be returned
 * If a user is in the context of a user group, the appropriate feature will be returned
 * If a feature does not have a valid id/sub, the sales route will be returned
 *
 * @returns An object containing functions that return the root routes for each feature group.
 */
const useFeatureRoutes = (): IFeatureRoutes => {
    const { orgGroup, orgGroupSlugWrapper } = useOrgGroupRoutes()

    const brewKnowledgeId = useAppSelector(selectActivePpSubId)
    const brewMenuId = useAppSelector(selectActiveBmSubId)
    const marketMyBreweryId = useAppSelector(selectActiveBrandId)
    const performancePlatformId = useAppSelector(selectActivePpSubId)

    // If an orgGroup is selected, the route will default to the application root
    // If an id is available in the org group, the feature route will be returned
    // Otherwise, the sales route will be returned
    const brewKnowledgeFeatureRoute = () => orgGroupSlugWrapper(`bk/${salesOrId(brewKnowledgeId)}`)
    const brewMenuFeatureRoute = () => orgGroupSlugWrapper(`bm/${salesOrId(brewMenuId)}`)
    const marketMyBreweryFeatureRoute = () =>
        orgGroupSlugWrapper(`mmb/${salesOrId(marketMyBreweryId)}`)
    const performancePlatformFeatureRoute = () =>
        orgGroupSlugWrapper(`pp/${salesOrId(performancePlatformId)}`)

    const brewMenuFeatureSubscriptionRoute = (group: OrganizationGroup) =>
        `${orgGroup(group?.slug)}/bm/${group?.bmSubId}`
    const marketMyBreweryFeatureSubscriptionRoute = (group: OrganizationGroup) =>
        `${orgGroup(group.slug)}/mmb/${group.brandId}`
    const performancePlatformSubscriptionRoute = (group: OrganizationGroup) =>
        `${orgGroup(group.slug)}/pp/${group.ppSubId}`
    const brewKnowledgeSubscriptionRoute = (group: OrganizationGroup) =>
        `${orgGroup(group.slug)}/bk/${group.ppSubId}`

    return {
        brewKnowledgeFeatureRoute,
        brewKnowledgeSubscriptionRoute,
        brewMenuFeatureRoute,
        brewMenuFeatureSubscriptionRoute,
        marketMyBreweryFeatureRoute,
        marketMyBreweryFeatureSubscriptionRoute,
        performancePlatformFeatureRoute,
        performancePlatformSubscriptionRoute
    }
}

export default useFeatureRoutes
